import { z } from 'zod';

/** Codes that collide with central hostnames under ROOT_DOMAIN (brief §0). */
export const RESERVED_TENANT_CODES = [
  'API',
  'ADMIN',
  'APP',
  'WWW',
  'MAIL',
  'STATUS',
  'VPN',
] as const;

/** The official demo tenant; only Mantle may register it. */
export const DEMO_TENANT_CODE = 'DEMO';

export const tenantCodeSchema = z
  .string()
  .regex(/^[A-Z0-9]{2,12}$/, 'Tenant code must be 2–12 uppercase letters or digits')
  .refine((code) => !(RESERVED_TENANT_CODES as readonly string[]).includes(code), {
    message: 'This tenant code is reserved',
  });

export const hexColorSchema = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, 'Expected a colour like #0B3D91');

export const httpsUrlSchema = z.url({ protocol: /^https$/ });

/** Relative path ("/api/v1/...") or absolute https URL. */
export const assetUrlSchema = z.union([z.string().regex(/^\/[^\s]*$/), httpsUrlSchema]);

export const emailSchema = z.email();

export const platformSchema = z.enum(['ios', 'android', 'web']);
export type Platform = z.infer<typeof platformSchema>;

/** Error envelope used by every API (brief §5.3). */
export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    details: z.unknown().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

export const ERROR_CODES = {
  validation: 'VALIDATION_ERROR',
  unauthenticated: 'UNAUTHENTICATED',
  forbidden: 'FORBIDDEN',
  notFound: 'NOT_FOUND',
  conflict: 'CONFLICT',
  rateLimited: 'RATE_LIMITED',
  tenantSuspended: 'TENANT_SUSPENDED',
  escalation: 'ROLE_ESCALATION',
  tenantAdminOnly: 'TENANT_ADMIN_ONLY',
  impersonationReadOnly: 'IMPERSONATION_READ_ONLY',
  impersonationExpired: 'IMPERSONATION_EXPIRED',
  /** 403: a module write while developer view-as (X-TP-View-As) is in use. */
  viewAsReadOnly: 'VIEW_AS_READ_ONLY',
  internal: 'INTERNAL_ERROR',
  /** 503: a dependency (e.g. the realm's JWKS) is briefly unreachable. Retry; not a sign-out. */
  unavailable: 'SERVICE_UNAVAILABLE',
  /** 503: an integration the school hasn't set up yet (e.g. PowerSchool settings incomplete). */
  notConfigured: 'NOT_CONFIGURED',
  /**
   * 409: the school's Odoo lacks a model, field or access right a tile needs (Odoo plan §5.1);
   * `details` is `odooNotCompatibleDetailsSchema`. Nothing else was called.
   */
  odooNotCompatible: 'ODOO_NOT_COMPATIBLE',
  /**
   * 403 on DELETE /api/v1/me: the token's sign-in (`auth_time`) is older than 10 minutes.
   * The app signs the user in again (prompt=login) and retries. Not a sign-out (403, not 401,
   * so the app's 401 refresh-and-sign-out path never runs).
   */
  recentSignInRequired: 'RECENT_SIGN_IN_REQUIRED',
  /** 409: deleting this account would leave the school with no Tenant Admin. */
  lastTenantAdmin: 'LAST_TENANT_ADMIN',
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export function makeError(code: ErrorCode, message: string, details?: unknown): ErrorEnvelope {
  return details === undefined
    ? { error: { code, message } }
    : { error: { code, message, details } };
}
